/**
 * Playback init + validate — admin frontend mirror of the mobile app's flow.
 *
 *   1. `playbackInit()` generates a one-shot client AES key, RSA-encrypts it
 *      with the server public key (already fetched on login bootstrap), and
 *      POSTs `{a, p}` to `/playback/init`. The server responds with its own
 *      AES key/IV encrypted *with our client key* and a per-language random
 *      suffix appended for light obfuscation (5 chars on `a`, 11 on `p`).
 *
 *   2. `playbackValidate(mediaId)` POSTs `{userId, mediaId, platform}` and
 *      gets back the media — with transcript and long-description fields
 *      still AES-GCM-encrypted under the server playback key.
 *
 *   3. `getPlaybackKey()` is the cached unwrap helper. The first call after
 *      init decrypts the server key/IV and memoises them for the rest of the
 *      session.
 */

import { api } from '../api';
import { session } from '../auth';
import { encryptFields, hasPublicKey } from '../encryption';
import { bootstrapPublicKey } from './auth';
import { decryptAesGcm, generateAesKeyAndIv, type ClientAesKey } from '../playbackCrypto';
import type { Page } from '../../types/api';

const SUFFIX_KEY = 5;
const SUFFIX_IV = 11;

interface InitResponse {
  a: string; // encrypted server AES key (+ random suffix)
  p: string; // encrypted server AES IV (+ random suffix)
}

export interface PlaybackKey {
  key: string;
  keyIV: string;
}

let clientKey: ClientAesKey | null = null;
let initPromise: Promise<void> | null = null;
let cachedPlaybackKey: PlaybackKey | null = null;
let initResponse: InitResponse | null = null;

export function isPlaybackInitialized(): boolean {
  return !!initResponse;
}

export function resetPlaybackKey(): void {
  clientKey = null;
  initResponse = null;
  initPromise = null;
  cachedPlaybackKey = null;
}

async function playbackInit(): Promise<void> {
  if (!hasPublicKey()) await bootstrapPublicKey();
  clientKey = await generateAesKeyAndIv();
  const payload = encryptFields({ a: clientKey.keyBase64, p: clientKey.ivBase64 });
  initResponse = await api.post<InitResponse>('/api/playback/init', payload);
}

export async function ensurePlaybackInit(): Promise<void> {
  if (initResponse) return;
  if (!initPromise) initPromise = playbackInit().finally(() => { initPromise = null; });
  await initPromise;
}

export async function getPlaybackKey(): Promise<PlaybackKey> {
  if (cachedPlaybackKey) return cachedPlaybackKey;
  await ensurePlaybackInit();
  if (!initResponse || !clientKey) throw new Error('Playback init did not complete');
  const key = await decryptAesGcm(
    initResponse.a.substring(0, initResponse.a.length - SUFFIX_KEY),
    clientKey.keyBase64,
    clientKey.ivBase64,
  );
  const keyIV = await decryptAesGcm(
    initResponse.p.substring(0, initResponse.p.length - SUFFIX_IV),
    clientKey.keyBase64,
    clientKey.ivBase64,
  );
  cachedPlaybackKey = { key, keyIV };
  return cachedPlaybackKey;
}

export interface PlaybackResponse {
  canPlay?: boolean;
  source?: unknown;
  transcript?: {
    text?: string;
    textEn?: string;
    textPt?: string;
    textEs?: string;
    textFr?: string;
  };
  longDescription?: string;
  longDescriptionPt?: string;
  longDescriptionEs?: string;
  longDescriptionFr?: string;
  code?: string;
}

export async function playbackValidate(mediaId: string): Promise<PlaybackResponse> {
  await ensurePlaybackInit();
  const userId = session.user?.id;
  return api.post<PlaybackResponse>('/api/playback/validate', {
    userId,
    mediaId,
    platform: 'web',
  });
}

/**
 * Decrypt every transcript + longDescription field returned by /playback/validate.
 *
 * Per-field tolerant, mirroring the mobile app: a field that fails to decrypt
 * keeps its raw value instead of failing the whole payload. The server sends
 * `transcript.text` ALREADY decrypted for platform=web + Oju Odu media (the
 * rest stay encrypted), so a plaintext field here is expected, not an error.
 */
export async function decryptPlaybackPayload(resp: PlaybackResponse): Promise<{
  transcript: { text?: string; textEn?: string; textPt?: string; textEs?: string; textFr?: string };
  longDescription?: string;
  longDescriptionPt?: string;
  longDescriptionEs?: string;
  longDescriptionFr?: string;
}> {
  const { key, keyIV } = await getPlaybackKey();
  const out: any = { transcript: {} };
  const transcriptFields = ['text', 'textEn', 'textPt', 'textEs', 'textFr'] as const;
  for (const f of transcriptFields) {
    const v = resp.transcript?.[f];
    if (!v) continue;
    out.transcript[f] = v;
    try {
      out.transcript[f] = await decryptAesGcm(v, key, keyIV);
    } catch (e) {
      // One undecryptable field must not strand the remaining languages.
      console.warn(`playback decrypt: keeping raw value for transcript.${f}`, e);
    }
  }
  const descFields = ['longDescription', 'longDescriptionPt', 'longDescriptionEs', 'longDescriptionFr'] as const;
  for (const f of descFields) {
    const v = resp[f];
    if (!v) continue;
    out[f] = v;
    try {
      out[f] = await decryptAesGcm(v, key, keyIV);
    } catch (e) {
      console.warn(`playback decrypt: keeping raw value for ${f}`, e);
    }
  }
  return out;
}

// ── Playback error logs (admin) ──────────────────────────────────────────

/** A client-reported playback failure (mirrors the backend PlaybackErrorLog). */
export interface PlaybackErrorLog {
  id: string;
  createdAt?: string;
  serverTimestamp?: string;
  serverPlatform?: string;
  serverClientId?: string;
  deviceId?: string;
  appId?: string;
  appVersion?: string;
  platform?: string;
  userId?: string;
  mediaId?: string;
  classEpisode?: boolean;
  subscriptionActive?: boolean;
  hasOriginalTransactionId?: boolean;
  stage?: string;
  shakaSupported?: boolean;
  hasPlaybackKey?: boolean;
  sourceUri?: string;
  format?: string;
  shakaErrorCode?: string;
  shakaErrorCategory?: string;
  shakaErrorMessage?: string;
  shakaErrorData?: string;
  errorMessage?: string;
  userAgent?: string;
  clientTimestamp?: string;
  extra?: Record<string, unknown>;
}

export interface PlaybackErrorFilters {
  platform?: string;
  stage?: string;
}

/** Admin: paged, newest-first playback error logs (optionally filtered). */
export async function listPlaybackErrors(
  { page, size, filters }: { page: number; size: number; filters: PlaybackErrorFilters },
): Promise<Page<PlaybackErrorLog>> {
  const res = await api.post<{ items: PlaybackErrorLog[]; total: number; page: number; size: number }>(
    '/api/playback/errors',
    { page, size, platform: filters.platform || undefined, stage: filters.stage || undefined },
  );
  return { data: res.items ?? [], page: res.page, size: res.size, totalCount: res.total };
}
